import { assertUnreachable } from '@sgme/fp';

import type { BulkCashClientWay, BulkSwapClientWay } from '@/store/bulkTrade/state.ts';

/**
 * Client way of a bulk leg, whatever its product.
 *
 * A swap way describes the two legs at once: 'BuyThenSell' means the client buys the negotiated
 * currency on the near leg, then sells it back on the far leg. The near leg therefore carries the
 * direction, which is what makes the cash <-> swap conversions below lossless.
 */
export type BulkClientWay = BulkCashClientWay | BulkSwapClientWay;

/** Convert any client way to its swap form, keeping the direction of the near leg. */
export function toSwapWay(way: BulkClientWay): BulkSwapClientWay {
  switch (way) {
    case 'Buy':
    case 'BuyThenSell':
      return 'BuyThenSell';

    case 'Sell':
    case 'SellThenBuy':
      return 'SellThenBuy';

    default:
      return assertUnreachable(way, `Unknown way: ${way}`);
  }
}

/** Convert any client way to its cash form, i.e. the direction of the near leg for a swap. */
export function toCashWay(way: BulkClientWay): BulkCashClientWay {
  switch (way) {
    case 'Buy':
    case 'BuyThenSell':
      return 'Buy';

    case 'Sell':
    case 'SellThenBuy':
      return 'Sell';

    default:
      return assertUnreachable(way, `Unknown way: ${way}`);
  }
}

export function oppositeSwapWay(way: BulkSwapClientWay): BulkSwapClientWay {
  switch (way) {
    case 'BuyThenSell':
      return 'SellThenBuy';

    case 'SellThenBuy':
      return 'BuyThenSell';

    default:
      return assertUnreachable(way, `Unknown swap way: ${way}`);
  }
}

export function oppositeCashWay(way: BulkCashClientWay): BulkCashClientWay {
  switch (way) {
    case 'Buy':
      return 'Sell';

    case 'Sell':
      return 'Buy';

    default:
      return assertUnreachable(way, `Unknown cash way: ${way}`);
  }
}
